import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
	DialogClose,
} from "@/components/ui/dialog";
import { generateSprints, type Sprint } from "@/services/sprint.service";
import { toast } from "sonner";

const DEFAULT_WEEKS = 4;
const MAX_WEEKS = 52;

export function GenerateSprintsDialog({
	open,
	onClose,
	onGenerated,
}: {
	open: boolean;
	onClose: () => void;
	onGenerated: (sprints: Sprint[]) => void;
}) {
	const [weeks, setWeeks] = useState(String(DEFAULT_WEEKS));
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function handleOpenChange(isOpen: boolean) {
		if (isOpen) return;
		setWeeks(String(DEFAULT_WEEKS));
		setError(null);
		onClose();
	}

	async function handleSubmit() {
		const n = Number(weeks);
		if (!Number.isInteger(n) || n < 1 || n > MAX_WEEKS) {
			setError(`Enter a whole number between 1 and ${MAX_WEEKS}.`);
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			const sprints = await generateSprints(n);
			onGenerated(sprints);
			toast.success(`${sprints.length} sprint${sprints.length === 1 ? "" : "s"} generated`);
			handleOpenChange(false);
		} catch (err) {
			const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
			setError(msg ?? "Failed to generate sprints.");
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="max-w-[400px]">
				<DialogHeader>
					<DialogTitle>Auto generate sprints</DialogTitle>
					<DialogDescription>
						Creates one sprint per work week (Mon - Fri), starting after the latest sprint.
					</DialogDescription>
				</DialogHeader>

				<div>
					<label
						htmlFor="generate-weeks"
						className="text-sm font-medium text-muted-foreground mb-1.5 block"
					>
						How many weeks to generate
					</label>
					<Input
						id="generate-weeks"
						type="number"
						min={1}
						max={MAX_WEEKS}
						value={weeks}
						onChange={(e) => {
							setWeeks(e.target.value);
							setError(null);
						}}
						onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
						className={error ? "border-danger" : ""}
					/>
					{error && <p className="text-xs text-danger mt-1">{error}</p>}
				</div>

				<DialogFooter>
					<DialogClose asChild>
						<Button variant="outline" disabled={submitting}>
							Cancel
						</Button>
					</DialogClose>
					<Button onClick={handleSubmit} disabled={submitting}>
						{submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
						Generate
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
